import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ParsedList, Playlist, PlaylistEntry } from '@visious/core';
import { AUTH_QUERY_KEY, type AuthApiProblem } from '../api/auth';
import { fetchOwnedPlaylists } from '../api/playlists';
import { getPlatform } from '../platform/instance';
import { useSession } from '../platform/session';

/**
 * `GET /api/v1/auth/playlists`, and the one rule that matters about it: **it is expensive and nothing
 * may fetch it eagerly.**
 *
 * The endpoint embeds the first 100 entries of *every* playlist, has no summary form, no paging, and
 * ignores `?fields=` — measured, all four. So the response is roughly 1.2 KB per entry across the whole
 * collection: six playlists with 125 entries between them came to 130 KB, and ten playlists of a hundred
 * videos is on the order of a megabyte. That is the cost of drawing a list of titles.
 *
 * Two consequences, both encoded here rather than left to each caller:
 *
 * - `enabled` is a parameter. The Playlists page passes `true` (the list *is* the page); the
 *   add-to-playlist menu on Watch passes "the menu is open", so a reader who never opens it never pays.
 * - `staleTime` is long. The set of playlists you own changes when *you* change it, and every change
 *   goes through the mutations below — which update or invalidate this cache precisely. A time-based
 *   refetch would re-download the megabyte to learn nothing.
 */

export const OWNED_PLAYLISTS_KEY = 'ownedPlaylists';

/** A failure is a value, not a throw — the transport returns its errors (1c). */
export type OwnedPlaylistsResult =
  | { readonly ok: true; readonly list: ParsedList<Playlist> }
  | { readonly ok: false; readonly problem: AuthApiProblem };

export function ownedPlaylistsKey(grantKey: number): readonly unknown[] {
  return [AUTH_QUERY_KEY, OWNED_PLAYLISTS_KEY, grantKey];
}

async function loadOwnedPlaylists(): Promise<OwnedPlaylistsResult> {
  const boot = await getPlatform();
  if (!boot.ok) return { ok: false, problem: { kind: 'bad-url', path: '/api/v1/auth/playlists' } };

  // `null` scopes for `useSubscriptions.ts`'s reason: on a *read*, the instance is the authority and
  // the cost of pre-refusing wrongly is a page that will not load against a grant that would have
  // worked. The writes below do pass the grant, because there the cost runs the other way.
  const result = await fetchOwnedPlaylists(boot.platform, null);
  return result.ok ? { ok: true, list: result.value } : { ok: false, problem: result.error };
}

export interface OwnedPlaylistsQuery {
  readonly playlists: readonly Playlist[] | null;
  readonly droppedCount: number;
  readonly problem: AuthApiProblem | null;
  readonly loading: boolean;
  readonly refreshing: boolean;
  readonly refetch: () => void;
  /** The grant's own timestamp, so a caller can build the same key for an invalidation. */
  readonly grantKey: number;
}

export function useOwnedPlaylists(enabled: boolean): OwnedPlaylistsQuery {
  const session = useSession();
  // The credential's timestamp is part of the identity, 3a's rule: a *failure* is cached as a value
  // here, so a refusal caused by the previous token would otherwise be served to the new one.
  const grantKey = session.grantedAtEpochSec ?? 0;
  const active = enabled && session.ready && session.facts.signedIn;

  const query = useQuery({
    queryKey: ownedPlaylistsKey(grantKey),
    queryFn: () => loadOwnedPlaylists(),
    retry: false,
    staleTime: 30 * 60 * 1000,
    enabled: active,
  });

  return {
    playlists: query.data?.ok === true ? query.data.list.items : null,
    droppedCount: query.data?.ok === true ? query.data.list.dropped.length : 0,
    problem:
      query.error !== null
        ? { kind: 'transport', error: { kind: 'network', status: null, url: '', retryable: true } }
        : query.data?.ok === false
          ? query.data.problem
          : null,
    loading: active && query.isPending,
    refreshing: query.isFetching && !query.isPending,
    refetch: () => void query.refetch(),
    grantKey,
  };
}

/* ------------------------------------------------------------------------------------------------
 * Keeping the cache honest after a write
 * ---------------------------------------------------------------------------------------------- */

/**
 * The playlist page's own query key, for the authenticated read. Distinct from the public page's
 * `['playlist', id]` on purpose: the two endpoints answer differently for a private playlist, so one key
 * for both would serve a `404` from the cache to a request that would have succeeded.
 */
export function authPlaylistKey(playlistId: string, grantKey: number): readonly unknown[] {
  return [AUTH_QUERY_KEY, 'playlist', playlistId, grantKey];
}

/**
 * Append an entry to a cached playlist **in place**, rather than invalidating the owned list.
 *
 * This is the one cache update worth hand-writing, and the reason is the cost above: the
 * add-to-playlist menu is open when the add happens, so the list query is *mounted* — an ordinary
 * `invalidateQueries` would refetch the whole megabyte to learn one row. The `201` already carries the
 * complete entry (`parseAddedPlaylistEntry`), so there is nothing to go and ask for.
 *
 * `videoCount` is bumped alongside, because it is what the row renders — see `toOwnedPlaylistRow` for
 * why the count rather than `entries.length`.
 */
export function noteEntryAdded(
  client: QueryClient,
  grantKey: number,
  playlistId: string,
  entry: PlaylistEntry,
): void {
  client.setQueryData<OwnedPlaylistsResult>(ownedPlaylistsKey(grantKey), (previous) => {
    if (previous?.ok !== true) return previous;
    return {
      ok: true,
      list: {
        ...previous.list,
        items: previous.list.items.map((playlist) =>
          playlist.playlistId === playlistId
            ? {
                ...playlist,
                // Only when the embedded window was complete. Appending to a truncated list of 100
                // would put the new row at position 100 of a 340-entry playlist, which is a lie about
                // where it landed — and `playlistMembership` reads the length to decide whether it can
                // answer at all, so a padded array would make "unknown" read as "absent".
                entries:
                  playlist.videoCount !== null && playlist.entries.length >= playlist.videoCount
                    ? [...playlist.entries, entry]
                    : playlist.entries,
                videoCount: playlist.videoCount === null ? null : playlist.videoCount + 1,
              }
            : playlist,
        ),
      },
    };
  });
}

/**
 * Mark the owned list stale **without refetching it**.
 *
 * `refetchType: 'none'` is the whole point. After removing an entry, the list's embedded copy of that
 * playlist is out of date and must not be trusted — but the reader is on the playlist page, not the
 * list, so refetching now spends a megabyte on a page nobody is looking at. Marked stale, it is correct
 * the next time it is actually read.
 */
export function markOwnedPlaylistsStale(client: QueryClient, grantKey: number): void {
  void client.invalidateQueries({ queryKey: ownedPlaylistsKey(grantKey), refetchType: 'none' });
}

/** Both reads of one playlist — the public page's and the authenticated one — after it changed. */
export function invalidatePlaylist(client: QueryClient, grantKey: number, playlistId: string): void {
  void client.invalidateQueries({ queryKey: authPlaylistKey(playlistId, grantKey) });
  void client.invalidateQueries({ queryKey: ['playlist', playlistId] });
}

export function useQueryClientRef(): QueryClient {
  return useQueryClient();
}
